import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Carousel from "../../common/Carousel";
import useAboutAnalytics from "./useAboutAnalytics";
import { AboutSeoMeta } from "./AboutSeoMeta";
import {
  Section, BgBlob, DotGrid, Inner,
  TwoCol, MediaCol,
  ContentCol, Eyebrow, Title, SubTitle, Divider,
  SubText, SubTextLight,
  StatRow, StatPill, StatNumber, StatLabel,
  CtaBtn, BtnRow,
} from "./About.styles";

// ─── Icon atoms ────────────────────────────────────────────────────────────────

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ─── Stats data ───────────────────────────────────────────────────────────────

const STATS = [
  { number: "8+",   label: "Years of\nPractice" },
  { number: "2,400+", label: "Happy\nStudents"     },
  { number: "4.9★", label: "Average\nRating"     },
  { number: "98%",  label: "Would\nRecommend"    },
];

// ─── About ────────────────────────────────────────────────────────────────────

const About = () => {
  const navigate  = useNavigate();
  const analytics = useAboutAnalytics();

  // ── Section view on mount ──
  useEffect(() => {
    analytics.trackSectionView(window.location.href);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBookTrial = useCallback(() => {
    analytics.trackBookTrialClick();
    navigate("/contactUs");
  }, [analytics, navigate]);

  return (
    <Section id="about">

      {/* ── SEO — AboutSeoMeta mirrors BlogListMeta pattern ── */}
      <AboutSeoMeta />

      {/* Background decoration */}
      <BgBlob className="b1" aria-hidden="true" />
      <BgBlob className="b2" aria-hidden="true" />
      <BgBlob className="b3" aria-hidden="true" />
      <DotGrid aria-hidden="true" />

      <Inner>
        <TwoCol>
          {/* ── Left: carousel / media ── */}
          <MediaCol>
            <Carousel />
          </MediaCol>

          {/* ── Right: content ── */}
          <ContentCol>
            <Eyebrow>✦ About Us</Eyebrow>

            <Title>Welcome To The</Title>
            <SubTitle>Yoga Shastra</SubTitle>

            <Divider aria-hidden="true" />

            <SubText>
              The aim of Yoga Shastra is to cultivate physical and mental health,
              achieve inner peace, and connect with the deeper aspects of the self.
              We integrate mind, body, and spirit — bringing harmony and balance to
              every dimension of your being.
            </SubText>

            <SubTextLight>
              A skilled yoga trainer provides personalised instruction and
              modifications based on your needs, goals, and abilities — making yoga
              accessible and transformative for practitioners of every level.
            </SubTextLight>

            {/* Inline stat pills */}
            <StatRow aria-label="Studio highlights">
              {STATS.map(({ number, label }) => (
                <StatPill key={label}>
                  <StatNumber>{number}</StatNumber>
                  <StatLabel>{label}</StatLabel>
                </StatPill>
              ))}
            </StatRow>

            {/* CTA buttons */}
            <BtnRow>
              <CtaBtn
                type="button"
                onClick={handleBookTrial}
                aria-label="Book a free trial class"
              >
                Book a Trial <ArrowIcon />
              </CtaBtn>
            </BtnRow>
          </ContentCol>
        </TwoCol>
      </Inner>
    </Section>
  );
};

About.displayName = "About";
export default React.memo(About);